import React, { useState, useEffect, useCallback, useRef } from 'react';
import { PlayCircle, Search } from 'lucide-react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent } from '../../../components/ui/dialog';
import { Skeleton } from '../../../components/ui/skeleton';
import { cn } from '../../../lib/utils';
import FolderAskAI from './FolderAskAI';
import { fetchFileSystem } from '../../../services/fileSystemService';
import { getFileUrl } from '../../../lib/routingUtils';
import useDebounce from '../../../hooks/useDebounce';
import type { FileType, FolderType } from '../../../types';
import type { RootState } from '../../../reducers';

interface FolderModalProps {
  open: boolean;
  onClose: () => void;
  folder: FolderType;
}

const FolderModal: React.FC<FolderModalProps> = ({ open, onClose, folder }) => {
  const [files, setFiles] = useState<FileType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalFiles, setTotalFiles] = useState(0);
  const [hasMoreFiles, setHasMoreFiles] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isNearBottom, setIsNearBottom] = useState(false);
  const [shouldLoadMore, setShouldLoadMore] = useState(false);
  const [preloadedPages, setPreloadedPages] = useState<Set<number>>(new Set());
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [hadFilesOnOpen, setHadFilesOnOpen] = useState(false);

  const navigate = useNavigate();
  const { team } = useSelector((state: RootState) => state);
  const teamId = team.selectedTeam?.teamId;

  const ITEMS_PER_PAGE = 20;
  const debouncedShouldLoadMore = useDebounce(shouldLoadMore, 50);
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // Function to format duration from seconds to MM:SS format
  const formatDuration = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const fetchFolderFiles = useCallback(
    async (page: number, append: boolean = false, search?: string) => {
      if (!teamId) return;

      try {
        if (!append) {
          setLoading(true);
        } else {
          setIsLoadingMore(true);
        }
        setError(null);

        const response = await fetchFileSystem({
          teamId,
          folderId: folder._id,
          page,
          limit: ITEMS_PER_PAGE,
          searchQuery: search || searchQuery || undefined,
        });

        if (response.data.success) {
          const newFiles = response.data.data.fileSystem;
          const total = response.data.data.pagination?.total || newFiles.length;

          if (append) {
            setFiles((prev) => [...prev, ...newFiles]);
          } else {
            setFiles(newFiles);
          }

          setTotalFiles(total);
          setCurrentPage(page);
          setHasMoreFiles(
            newFiles.length === ITEMS_PER_PAGE &&
              files.length + newFiles.length < total,
          );
          setPreloadedPages((prev) => new Set([...prev, page]));
        } else {
          setError('Failed to load folder contents');
        }
      } catch (err) {
        console.error('Error fetching folder files:', err);
        setError('Failed to load folder contents');
      } finally {
        setLoading(false);
        setIsLoadingMore(false);
      }
    },
    [teamId, folder._id, ITEMS_PER_PAGE, files.length, searchQuery],
  );

  // Initial fetch when modal opens
  useEffect(() => {
    if (!open || !teamId) return;

    // Reset state when modal opens
    setFiles([]);
    setCurrentPage(1);
    setTotalFiles(0);
    setHasMoreFiles(true);
    setPreloadedPages(new Set());
    setSearchQuery('');
    setHadFilesOnOpen(false);

    const initialFetch = async () => {
      await fetchFolderFiles(1, false);
    };
    initialFetch();
  }, [open, folder._id, teamId]);

  // Track when we first get files
  useEffect(() => {
    if (files.length > 0 && !hadFilesOnOpen) {
      setHadFilesOnOpen(true);
    }
  }, [files.length, hadFilesOnOpen]);

  // Refetch when debounced search query changes
  useEffect(() => {
    if (!open || !teamId) return;

    // Don't trigger on initial render
    if (debouncedSearchQuery === '' && files.length === 0 && !hadFilesOnOpen) {
      return;
    }

    // Reset pagination state when search changes
    setCurrentPage(1);
    setTotalFiles(0);
    setHasMoreFiles(true);
    setPreloadedPages(new Set());

    fetchFolderFiles(1, false, debouncedSearchQuery);
  }, [debouncedSearchQuery]);

  // Scroll handling for infinite loading
  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const target = event.currentTarget;
      const { scrollHeight, scrollTop, clientHeight } = target;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

      // Preload when user is 2 viewport heights away from bottom for seamless scrolling
      const preloadThreshold = window.innerHeight * 2;
      // Show skeleton only when very close to bottom (half viewport)
      const skeletonThreshold = window.innerHeight * 0.5;

      // Update near-bottom state for skeleton display
      setIsNearBottom(distanceFromBottom < skeletonThreshold);

      if (
        distanceFromBottom < preloadThreshold &&
        !loading &&
        !isLoadingMore &&
        hasMoreFiles
      ) {
        const nextPage = currentPage + 1;
        const maxPages = Math.ceil(totalFiles / ITEMS_PER_PAGE);

        // Prevent duplicate page requests
        if (nextPage > maxPages || preloadedPages.has(nextPage)) {
          return;
        }

        // Trigger the debounced load
        setShouldLoadMore(true);
      } else {
        setShouldLoadMore(false);
      }
    },
    [
      loading,
      isLoadingMore,
      hasMoreFiles,
      currentPage,
      totalFiles,
      ITEMS_PER_PAGE,
      preloadedPages,
    ],
  );

  // Effect to handle the actual API call when debounced trigger fires
  useEffect(() => {
    if (debouncedShouldLoadMore && !isLoadingMore && hasMoreFiles) {
      const nextPage = currentPage + 1;
      const maxPages = Math.ceil(totalFiles / ITEMS_PER_PAGE);

      if (nextPage <= maxPages && !preloadedPages.has(nextPage)) {
        fetchFolderFiles(nextPage, true);
      }
    }
  }, [
    debouncedShouldLoadMore,
    isLoadingMore,
    hasMoreFiles,
    currentPage,
    totalFiles,
    ITEMS_PER_PAGE,
    preloadedPages,
    fetchFolderFiles,
  ]);

  const handleFileClick = (file: FileType) => {
    if (!teamId) {
      console.error('No team selected');
      return;
    }

    // Close modal before navigating
    onClose();

    const fileUrl = getFileUrl({
      teamId,
      parentId: folder._id,
      fileId: file._id,
    });
    navigate(fileUrl);
  };

  // Get file IDs for Ask AI
  const fileIds = files.map((file) => file._id);
  // Keep Ask AI panel visible if we had files initially, even during search loading
  const hasFiles =
    (!loading && !error && fileIds.length > 0) || (hadFilesOnOpen && !error);

  const renderFileItem = (file: FileType) => (
    <button
      key={file._id}
      onClick={() => handleFileClick(file)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleFileClick(file);
        }
      }}
      type="button"
      className="w-full text-left px-6 py-2.5 mb-0.5 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center gap-3"
    >
      {/* Thumbnail */}
      <div className="relative flex-shrink-0">
        {file.thumbUrl ? (
          <img
            src={file.thumbUrl}
            alt={file.name}
            className="w-12 h-8 object-cover rounded bg-gray-100 dark:bg-gray-800"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
              const wrapper = target.parentElement!;
              wrapper.className =
                'w-12 h-8 rounded bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0';
              wrapper.innerHTML =
                '<svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><polygon points="10 8 16 12 10 16 10 8"/></svg>';
            }}
          />
        ) : (
          <div className="w-12 h-8 rounded bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
            <PlayCircle className="w-4 h-4 text-gray-400" />
          </div>
        )}
        {/* Duration overlay */}
        {file.duration && (
          <div className="absolute bottom-0.5 right-0.5 bg-black/80 text-white px-1 py-0.5 rounded text-[10px] font-medium">
            {formatDuration(file.duration)}
          </div>
        )}
      </div>

      {/* File info */}
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm line-clamp-1">{file.name}</div>
        {file.description && (
          <div className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
            {file.description}
          </div>
        )}
      </div>
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className={cn(
          'h-[480px] max-h-[80vh] overflow-hidden focus:outline-none focus:ring-0 p-0 gap-0 flex flex-col',
          hasFiles ? 'sm:max-w-[900px]' : 'sm:max-w-[600px]',
        )}
      >
        {/* Search Header */}
        <div className="flex items-center gap-3 px-6 py-3 border-b border-gray-200 dark:border-gray-700">
          <Search className="h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder={`Search in ${folder.name}...`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 bg-transparent outline-none text-base"
          />
        </div>

        {/* Content - Split view when Ask AI is enabled */}
        <div className="flex-1 flex overflow-hidden">
          <div
            ref={scrollContainerRef}
            onScroll={handleScroll}
            className={cn(
              'overflow-y-auto overflow-x-hidden',
              hasFiles ? 'flex-1' : 'w-full',
            )}
          >
            {(() => {
              if (loading) {
                return (
                  <div className="pt-3">
                    <div className="space-y-2">
                      {Array.from({ length: 8 }, (_, i) => (
                        <div key={i} className="flex gap-3 px-6 py-2.5">
                          <Skeleton className="w-12 h-8 rounded flex-shrink-0" />
                          <div className="flex-1 space-y-1">
                            <Skeleton className="h-4 w-full max-w-[70%]" />
                            <Skeleton className="h-3 w-full max-w-[40%]" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              }

              if (error) {
                return (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center space-y-1">
                      <p className="text-sm font-medium text-destructive">
                        {error}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Please try refreshing the page
                      </p>
                    </div>
                  </div>
                );
              }

              if (files.length === 0) {
                return (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-sm text-gray-500">
                      {searchQuery
                        ? 'No recordings found'
                        : 'This folder is empty'}
                    </p>
                  </div>
                );
              }

              return (
                <div className="pt-3">
                  <div>{files.map((file) => renderFileItem(file))}</div>

                  {/* Loading skeleton for pagination */}
                  {isLoadingMore && isNearBottom && hasMoreFiles && (
                    <div className="space-y-2 py-2">
                      {Array.from({ length: 4 }, (_, i) => (
                        <div
                          key={`skeleton-${i}`}
                          className="flex gap-3 px-6 py-2.5"
                        >
                          <Skeleton className="w-12 h-8 rounded flex-shrink-0" />
                          <div className="flex-1 space-y-1">
                            <Skeleton className="h-4 w-full max-w-[70%]" />
                            <Skeleton className="h-3 w-full max-w-[40%]" />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>

          {/* Ask AI Panel - Auto-enabled when files are loaded */}
          {!loading && !error && teamId && fileIds.length > 0 && (
            <div className="w-[350px] flex-shrink-0">
              <FolderAskAI
                folderName={folder.name}
                fileIds={fileIds}
                teamId={teamId}
              />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FolderModal;
